// emailList.js — read a typed or pasted list of email addresses.
// ---------------------------------------------------------------------------
// v0.266.0. Brett: "yes it should take several addresses." The Share box's
// recipient field takes a list, and each address gets its own link — one link
// per person, so a reply says who wrote it and one person's link can be turned
// off without cutting off the rest.
//
// WHAT IT ACCEPTS. Whatever people actually paste: commas, semicolons (Outlook
// copies a To line with them), new lines, plain spaces, and the
// "Sam Check <sam@example.test>" form an address book gives. Inside angle
// brackets only the address counts; the name is dropped.
//
// WHAT IT REFUSES. Anything that is not an address is handed back in `invalid`
// rather than skipped. A silently dropped address is a person who never gets
// the link while the sender thinks they did.
//
// The same address twice (any case) is one person and one link.

const EMAIL = /^[^\s@<>,;]+@[^\s@<>,;]+\.[^\s@<>,;]+$/;

export function parseEmailList(text) {
  const emails = [];
  const invalid = [];
  const seen = new Set();
  const add = (raw) => {
    const s = raw.trim().replace(/^mailto:/i, '');
    if (!s) return;
    if (!EMAIL.test(s)) { invalid.push(s); return; }
    const key = s.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    emails.push(s);
  };
  // A quoted display name ("Lee, Pat") may hold a comma; it is never the
  // address, so it goes before splitting.
  for (const part of String(text || '').replace(/"[^"]*"/g, ' ').split(/[,;\n\r]+/)) {
    const bracketed = [...part.matchAll(/<([^<>]*)>/g)];
    if (bracketed.length) {
      for (const m of bracketed) add(m[1]);
      continue;
    }
    for (const word of part.split(/\s+/)) add(word);
  }
  return { emails, invalid };
}

// The field's text with one address put in or taken out, leaving every other
// part as the person typed it. Used by the team chips.
export function toggleEmail(text, email) {
  const target = String(email || '').trim().toLowerCase();
  if (!target) return text;
  const { emails } = parseEmailList(text);
  if (emails.some((e) => e.toLowerCase() === target)) {
    const kept = String(text || '').split(/[,;\n\r]+/)
      .filter((part) => !parseEmailList(part).emails.some((e) => e.toLowerCase() === target))
      .map((p) => p.trim()).filter(Boolean);
    return kept.join(', ');
  }
  const base = String(text || '').trim().replace(/[,;]\s*$/, '');
  return base ? `${base}, ${email.trim()}` : email.trim();
}
